import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Remove direct import
// import logo from '../../../public/assets/img/Logo-Straight.png';

const Header = ({ showTopBar = false, scrolled = false, headerHidden = false }) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(scrolled);
  const [isMainHeaderVisible, setIsMainHeaderVisible] = useState(!headerHidden);
  const [isTabletUp, setIsTabletUp] = useState(window.innerWidth >= 768);
  const prevScrollY = useRef(0);
  const isAnimatingRef = useRef(false);
  const [demoButtonHover, setDemoButtonHover] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState("hero");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Logo path using PUBLIC_URL
  const logoPath = `${process.env.PUBLIC_URL}/assets/img/Logo-Straight.png`;
  
  // Set active nav item based on current path
  useEffect(() => {
    if (location.pathname === '/') {
      // On home page, use hash if available or default to hero
      const hash = location.hash.replace('#', '');
      setActiveNavItem(hash || 'hero');
    } else if (location.pathname === '/digicloud') {
      setActiveNavItem('details');
    } else if (location.pathname === '/tallyprime') {
      setActiveNavItem('tallyprime');
    } else if (location.pathname === '/tally-software-service') {
      setActiveNavItem('tss');
    } else if (location.pathname === '/sharepro') {
      setActiveNavItem('sharepro');
    } else if (location.pathname === '/busy') {
      setActiveNavItem('busy');
    } else if (location.pathname.startsWith('/blog')) {
      setActiveNavItem('blog');
    }
  }, [location.pathname, location.hash]);

  // Removed handleLogout function
  
  // Check if tablet or larger on resize
  useEffect(() => {
    const handleResize = () => {
      setIsTabletUp(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingUp = prevScrollY.current > currentScrollY;
      
      // Prevent multiple animations from triggering simultaneously
      if (isAnimatingRef.current) return;
      
      if (currentScrollY > 100) {
        setIsScrolled(true);
        
        // When scrolling down
        if (!scrollingUp) {
          if (!isMainHeaderVisible) {
            isAnimatingRef.current = true;
            
            // Then show only the main header after a delay
            setTimeout(() => {
              setIsMainHeaderVisible(true);
              
              // Reset animation flag after all transitions
              setTimeout(() => {
                isAnimatingRef.current = false;
              }, 400);
            }, 300);
          }
        }
      } else {
        // At the top of the page
        setIsScrolled(false);
        setIsMainHeaderVisible(true);
      }
      
      prevScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    
    // Close sidebar when ESC key is pressed
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    
    // Prevent scrolling when sidebar is open
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [isMainHeaderVisible, isSidebarOpen]);

  // Add a custom style to the head for the Get Demo button and navigation
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .demo-btn:before {
        display: none !important;
        content: none !important;
        visibility: hidden !important;
        width: 0 !important;
        height: 0 !important;
      }
      .demo-btn:hover:before {
        display: none !important;
        visibility: hidden !important;
        width: 0 !important;
      }
      .demo-btn:hover {
        color: #ffffff !important;
        text-decoration: none !important;
        border-bottom: none !important;
      }
      
      /* Navigation hover effect styles */
      .navmenu a {
        position: relative !important;
        transition: color 0.3s ease !important;
        padding: 6px 10px !important;
        border-radius: 4px !important;
      }
      
      /* Remove the original underline effect */
      .navmenu a:before {
        display: none !important;
        content: none !important;
      }
      
      /* New hover effect with background color */
      .navmenu li:hover > a,
      .navmenu .active,
      .navmenu .active:focus {
        color: #7b00ff !important;
        background-color: rgba(123, 0, 255, 0.08) !important;
      }
      
      /* Desktop navigation dropdown styling */
      .navmenu .dropdown ul {
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.12) !important;
        border-radius: 8px !important;
        padding: 10px 0 !important;
      }
      
      .navmenu .dropdown ul li {
        margin: 0 !important;
      }
      
      .navmenu .dropdown ul a {
        padding: 8px 20px !important;
        font-size: 14px !important;
        font-weight: 500 !important;
        border-radius: 0 !important;
      }
      
      .navmenu .dropdown ul a:hover,
      .navmenu .dropdown ul .active:hover,
      .navmenu .dropdown ul li:hover > a {
        color: #7b00ff !important;
        background-color: rgba(123, 0, 255, 0.08) !important;
      }
      
      /* Remove default dropdown carets */
      .dropdown-toggle::after {
        display: none !important;
        content: none !important;
        border: none !important;
      }
      
      /* Desktop specific styles */
      @media (min-width: 1200px) {
        .desktop-nav {
          display: flex !important;
        }
        .mobile-toggle {
          display: none !important;
        }
      }
      
      /* Mobile Sidebar Styles */
      .mobile-toggle {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #333;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 5px;
        transition: all 0.3s;
        z-index: 999;
      }
      
      .mobile-toggle:hover {
        color: #7b00ff;
      }
      
      .sidebar-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 998;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
      }
      
      .sidebar-overlay.active {
        opacity: 1;
        visibility: visible;
      }
      
      .sidebar {
        position: fixed;
        top: 0;
        left: -280px;
        width: 280px;
        height: 100%;
        background-color: #fff;
        z-index: 999;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        overflow-y: auto;
        padding: 30px 20px;
      }
      
      .sidebar.active {
        left: 0;
      }
      
      .sidebar-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 30px;
      }
      
      .sidebar-logo img {
        max-height: 40px;
      }
      
      .sidebar-close {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #333;
        transition: all 0.3s;
      }
      
      .sidebar-close:hover {
        color: #7b00ff;
      }
      
      .sidebar-nav {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      
      .sidebar-nav li {
        margin-bottom: 15px;
      }
      
      .sidebar-nav a {
        color: #333;
        font-size: 16px;
        font-weight: 500;
        text-decoration: none;
        transition: all 0.3s;
        display: block;
        padding: 5px 0;
      }
      
      .sidebar-nav a:hover,
      .sidebar-nav a.active {
        color: #7b00ff;
      }
      
      .sidebar-dropdown {
        margin-top: 10px;
        padding-left: 15px;
        display: none;
      }
      
      .sidebar-dropdown.show {
        display: block;
      }
      
      .dropdown-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      
      .dropdown-icon {
        transition: transform 0.3s;
      }
      
      .dropdown-icon.rotate {
        transform: rotate(180deg);
      }
      
      .auth-buttons {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      
      /* Authentication button styles removed */
      
      .profile-dropdown {
        position: relative;
      }
      
      .profile-dropdown .dropdown-menu {
        position: absolute;
        right: 0;
        min-width: 180px;
        background-color: #fff;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        padding: 10px 0;
        margin-top: 10px;
        z-index: 1000;
      }
      
      .profile-dropdown .dropdown-item {
        padding: 8px 20px;
        color: #333;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.3s;
      }
      
      .profile-dropdown .dropdown-item:hover {
        background-color: rgba(123, 0, 255, 0.08);
        color: #7b00ff;
      }
      
      .profile-dropdown .dropdown-divider {
        height: 1px;
        margin: 8px 0;
        background-color: #e9ecef;
      }
      
      .avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: #7b00ff;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      .avatar:hover {
        background-color: #6a00e6;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Updated navigation click handler to work with React Router
  const handleNavClick = (section, path) => {
    setActiveNavItem(section);
    setIsSidebarOpen(false);
    
    // If it's a hash link on the current page, scroll to the section
    if (path && path.startsWith('/#')) {
      const element = document.getElementById(path.replace('/#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const toggleDropdown = (e, id) => {
    e.preventDefault();
    const dropdown = document.querySelector(`.sidebar-dropdown-${id}`);
    const icon = e.currentTarget.querySelector('.dropdown-icon');
    
    if (dropdown) {
      dropdown.classList.toggle('show');
      icon.classList.toggle('rotate');
    }
  };

  // Get Demo button styles
  const getDemoButtonStyle = {
    backgroundColor: demoButtonHover ? '#e85b17' : '#ff6b24',
    color: '#ffffff',
    padding: '8px 20px',
    borderRadius: '50px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    textDecoration: 'none !important',
    transform: demoButtonHover ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: demoButtonHover ? '0 4px 8px rgba(0, 0, 0, 0.2)' : '0 2px 5px rgba(0, 0, 0, 0.1)',
  };

  // Check if a link is active based on current location
  const isLinkActive = (path, id) => {
    if (path === '/' && location.pathname === '/') {
      return activeNavItem === id;
    }
    
    if (path === '/digicloud' && location.pathname === '/digicloud') {
      return true;
    }
    
    if (path === '/busy' && location.pathname === '/busy') {
      return true;
    }
    
    if (path === '/sharepro' && location.pathname === '/sharepro') {
      return true;
    }
    
    if (path === '/tallyprime' && location.pathname === '/tallyprime') {
      return true;
    }
    
    if (path === '/tally-software-service' && location.pathname === '/tally-software-service') {
      return true;
    }
    
    if (path === '/blog' && location.pathname.startsWith('/blog')) {
      return true;
    }
    
    if (path && path.includes('#') && location.pathname === '/') {
      const targetId = path.split('#')[1];
      return activeNavItem === targetId;
    }
    
    return false;
  };

  // Navigation items shared between desktop and mobile
  const navigationItems = [
    { id: 'hero', label: 'Home', path: '/' },
    { id: 'about', label: 'About', path: '/#about' },
    { 
      id: 'details', 
      label: 'Services',
      isDropdown: true,
      dropdownItems: [
        { id: 'tallyprime', label: 'TallyPrime', path: '/tallyprime' },
        { id: 'tss', label: 'Tally Software Service', path: '/tally-software-service' },
        { id: 'cloud', label: 'Cloud Computing', path: '/digicloud' },
        { id: 'sharepro', label: 'SharePro', path: '/sharepro' },
        { id: 'busy', label: 'Busy', path: '/busy' }
      ]
    },
    { id: 'blog', label: 'Blog', path: '/blog' },
    { id: 'team', label: 'Team', path: '/#team' },
    { id: 'pricing', label: 'Products', path: '/#pricing' }
  ];

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`} 
        onClick={toggleSidebar}
      ></div>
      
      {/* Mobile Sidebar */}
      <div className={`sidebar ${isSidebarOpen ? 'active' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Link to="/">
              <img src={logoPath} alt="Digisoft Logo" />
            </Link>
          </div>
          <button className="sidebar-close" onClick={toggleSidebar}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <ul className="sidebar-nav">
          {navigationItems.map((item) => (
            <li key={`mobile-${item.id}`}>
              {item.isDropdown ? (
                <>
                  <a 
                    href={item.path || `#${item.id}`} 
                    className={`dropdown-toggle ${isLinkActive(item.path, item.id) ? 'active' : ''}`}
                    onClick={(e) => toggleDropdown(e, item.id)}
                  >
                    {item.label}
                    <i className="bi bi-chevron-down dropdown-icon"></i>
                  </a>
                  <ul className={`sidebar-dropdown sidebar-dropdown-${item.id}`}>
                    {item.dropdownItems.map((dropdownItem, idx) => (
                      <li key={`mobile-dropdown-${item.id}-${idx}`}>
                        {dropdownItem.path.startsWith('/') && !dropdownItem.path.includes('#') ? (
                          <Link 
                            to={dropdownItem.path}
                            className={isLinkActive(dropdownItem.path, dropdownItem.id) ? 'active' : ''}
                            onClick={() => handleNavClick(dropdownItem.id)}
                          >
                            {dropdownItem.label}
                          </Link>
                        ) : (
                          <a 
                            href={dropdownItem.path || `#${dropdownItem.id}-${dropdownItem.hash}`}
                            className={isLinkActive(dropdownItem.path, dropdownItem.id) ? 'active' : ''}
                            onClick={() => handleNavClick(dropdownItem.id, dropdownItem.path)}
                          >
                            {dropdownItem.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                item.path.startsWith('/') && !item.path.includes('#') ? (
                  <Link 
                    to={item.path}
                    className={isLinkActive(item.path, item.id) ? 'active' : ''}
                    onClick={() => handleNavClick(item.id)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a 
                    href={item.path || `#${item.id}`} 
                    className={isLinkActive(item.path, item.id) ? 'active' : ''}
                    onClick={() => handleNavClick(item.id, item.path)}
                  >
                    {item.label}
                  </a>
                )
              )}
            </li>
          ))}
          <li className="mt-4">
            <a 
              href="#contact" 
              className="demo-btn" 
              style={{
                ...getDemoButtonStyle,
                width: '100%',
                textAlign: 'center',
                display: 'block'
              }}
              onMouseEnter={() => setDemoButtonHover(true)}
              onMouseLeave={() => setDemoButtonHover(false)}
            >
              Get Demo
            </a>
          </li>
          
          {/* Mobile Auth Buttons - Removed */}
        </ul>
      </div>

      {/* Main Header - Styled to match the provided HTML */}
      <header 
        id="header" 
        className="header d-flex align-items-center fixed-top" 
        style={{ 
          display: 'flex', 
          visibility: 'visible', 
          opacity: 1, 
          position: 'fixed',
          top: isTabletUp ? (isScrolled ? 0 : '41px') : 0,
          zIndex: 1030
        }}
      >
        <div className="container-fluid container-xl position-relative d-flex align-items-center justify-content-between">
          <Link to="/" className="logo d-flex align-items-center">
            <img src={logoPath} alt="Digisoft Logo" style={{ maxHeight: '50px' }} />
          </Link>
          
          {/* Mobile Toggle Button */}
          <button className="mobile-toggle" onClick={toggleSidebar}>
            <i className="bi bi-list"></i>
          </button>

          <nav id="navmenu" className="navmenu">
            {/* Desktop Navigation */}
            <ul className="desktop-nav">
              {navigationItems.map((item) => (
                <li key={`desktop-${item.id}`} className={item.isDropdown ? 'dropdown' : ''}>
                  {item.isDropdown ? (
                    <>
                      <a 
                        href={item.path || `#${item.id}`} 
                        className={`${isLinkActive(item.path, item.id) ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(item.id, item.path);
                        }}
                      >
                        {item.label} <i className="bi bi-chevron-down"></i>
                      </a>
                      <ul>
                        {item.dropdownItems.map((dropdownItem, idx) => (
                          <li key={`dropdown-${item.id}-${idx}`}>
                            {dropdownItem.path.startsWith('/') && !dropdownItem.path.includes('#') ? (
                              <Link 
                                to={dropdownItem.path}
                                className={isLinkActive(dropdownItem.path, dropdownItem.id) ? 'active' : ''}
                                onClick={() => handleNavClick(dropdownItem.id)}
                              >
                                {dropdownItem.label}
                              </Link>
                            ) : (
                              <a 
                                href={dropdownItem.path || `#${dropdownItem.id}-${dropdownItem.hash}`}
                                className={isLinkActive(dropdownItem.path, dropdownItem.id) ? 'active' : ''}
                                onClick={() => handleNavClick(dropdownItem.id, dropdownItem.path)}
                              >
                                {dropdownItem.label}
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    item.path.startsWith('/') && !item.path.includes('#') ? (
                      <Link 
                        to={item.path}
                        className={isLinkActive(item.path, item.id) ? 'active' : ''}
                        onClick={() => handleNavClick(item.id)}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a 
                        href={item.path || `#${item.id}`} 
                        className={isLinkActive(item.path, item.id) ? 'active' : ''}
                        onClick={() => handleNavClick(item.id, item.path)}
                      >
                        {item.label}
                      </a>
                    )
                  )}
                </li>
              ))}
              <li>
                <a 
                  href="#contact" 
                  className="demo-btn"
                  style={getDemoButtonStyle}
                  onMouseEnter={() => setDemoButtonHover(true)}
                  onMouseLeave={() => setDemoButtonHover(false)}
                >
                  Get Demo
                </a>
              </li>
              
              {/* Desktop Auth Buttons - Removed */}
            </ul>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;